import i18n from "../i18n";
// src/api/orders.js
// CANON: bids are under /api/orders/:id/bid (NOT /api/v2)
// Driver-frontend only.

const V2_BASE = import.meta.env.VITE_API_BASE || "/api/v2";

// Core (non-v2) base: same logic as auth.js (strip /v2)
// REISOR_STAGE22P2Q8B_DRIVER_ACCESS_LIMITED_UI_I18N_V1
const CORE_BASE = (() => {
  const explicit = import.meta.env.VITE_AUTH_BASE; // if set -> prefer it
  const base = String(explicit || V2_BASE || "/api").trim().replace(/\/+$/g, "");

  // If base ends with "/api/v2" -> "/api"
  if (/\/api\/v2$/i.test(base)) return base.replace(/\/api\/v2$/i, "/api");

  // If base ends with "/v2" -> strip it
  if (/\/v2$/i.test(base)) return base.replace(/\/v2$/i, "");

  // Otherwise assume already correct
  return base || "/api";
})();

const DRIVER_ACCESS_LIMITED_CODES = new Set([
  "DRIVER_ACCEPT_MISS_THRESHOLD",
  "DRIVER_BLOCKED_BY_MANUAL",
  "DRIVER_ACCESS_REVIEW_REQUIRED",
  "BLOCKED",
]);

function driverAccessLimitedMessage(data, code) {
  const c = String(code || data?.code || data?.error || "").toUpperCase();
  const platformStatus = String(data?.driverAccess?.status || data?.platformAccess?.status || "").toLowerCase();
  const blocked = DRIVER_ACCESS_LIMITED_CODES.has(c) || platformStatus === "blocked" || platformStatus === "review_required";
  if (!blocked) return "";
  return i18n.t("errors.DRIVER_ORDER_ACCESS_LIMITED", {
    defaultValue: "Доступ к заказам ограничен. Обратитесь в поддержку REISOR.",
  });
}

function readTokenFrom(storage) {
  const keys = ["driverToken", "drvToken", "driver_token", "token", "authToken"];
  for (const k of keys) {
    const v = storage.getItem(k);
    if (v) return v;
  }
  // sometimes stored as JSON
  try {
    const raw = storage.getItem("driverAuth") || storage.getItem("auth");
    if (raw) {
      const obj = JSON.parse(raw);
      return obj?.token || obj?.driverToken || obj?.authToken || null;
    }
  } catch {}
  // also stored as JSON in driver_data / driver
  try {
    const raw2 = storage.getItem("driver_data") || storage.getItem("driver");
    if (raw2) {
      const obj2 = JSON.parse(raw2);
      const tok2 =
        obj2?.token ||
        obj2?.accessToken ||
        obj2?.authToken ||
        obj2?.driverToken ||
        obj2?.jwt ||
        null;
      if (tok2) return tok2;
    }
  } catch {}

  return null;
}

function getDriverToken() {
  return (
    readTokenFrom(localStorage) ||
    readTokenFrom(sessionStorage) ||
    null
  );
}

function toBearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;
}

async function http(method, path, payload) {
  const token = getDriverToken();
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = toBearer(token);

  const res = await fetch(CORE_BASE + path, {
    method,
    headers,
    credentials: "include",
    body: method === "GET" ? undefined : JSON.stringify(payload || {}),
  });

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  let data;
  if (ct.includes("application/json")) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    try { data = await res.text(); } catch { data = ""; }
  }

  if (!res.ok) {
    let msg =
      (data && typeof data === "object" && (data.error || data.message)) ||
      (typeof data === "string" && data.slice(0, 160)) ||
      `HTTP ${res.status}`;

    const code =
      data && typeof data === "object"
        ? String(data.code || data.error || "").toUpperCase()
        : String(msg).toUpperCase();

    const accessLimited = driverAccessLimitedMessage(data, code);

    if (accessLimited) {
      msg = accessLimited;
    } else if (code === "NEGATIVE_BALANCE") {
      msg = i18n.t("errors.NEGATIVE_BALANCE");
    } else if (code === "DRIVER_ACCESS_REQUIRED") {
      msg = i18n.t("errors.DRIVER_ACCESS_REQUIRED");
    } else if (code === "DRIVER_PROFILE_NOT_APPROVED") {
      msg = i18n.t("errors.DRIVER_PROFILE_NOT_APPROVED");
    } else if (code === "DRIVER_WORK_STATUS_REQUIRED") {
      msg = i18n.t("errors.DRIVER_WORK_STATUS_REQUIRED", {
        defaultValue: "Укажите статус работы в анкете водителя.",
      });
    }

    const apiError = new Error(msg);
    apiError.name = "DriverOrdersApiError";
    apiError.code = code;
    apiError.status = res.status;
    apiError.data =
      data && typeof data === "object"
        ? data
        : null;

    throw apiError;
}

  return data;
}

/**
 * Place/update bid
 * Backend: POST /api/orders/:id/bid
 */
export async function placeBid(orderId, payload = {}) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("missing_order_id");

  const bidAmount =
    payload?.bidAmount != null ? payload.bidAmount : payload?.amount;

  return http("POST", `/orders/${encodeURIComponent(id)}/bid`, {
    ...payload,
    bidAmount,
  });
}

/**
 * Cancel bid
 * Backend: POST /api/orders/:id/bids/cancel
 */
export async function cancelBid(orderId, payload = {}) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("missing_order_id");

  return http("POST", `/orders/${encodeURIComponent(id)}/bids/cancel`, payload);
}
